import type { CSSProperties } from "react";
import {
  IconDental,
  IconPhone,
  IconPlaneArrival,
  IconPlaneDeparture,
} from "@tabler/icons-react";

import type { CalendarEvent } from "./calendar-types";
import { STATUS_HEX } from "@/lib/appointment-status";
import { arrivalVisitBucket } from "@/lib/calendar-events";

// Dentour 261015 — a first-visit arrival chip is WHITE (ARRIVAL_VISIT_FILL),
// so the chip's default white text and borderless edge would vanish into the
// grid: give it dark text and a hairline instead.
function whiteTravelChip(event: CalendarEvent): boolean {
  return arrivalVisitBucket(event.eventType === "arrival", event.feedMeta?.visit_ordinal) === 1;
}
const WHITE_CHIP_TEXT = "text-neutral-900";
const WHITE_CHIP_EDGE: CSSProperties = { border: "1px solid #d4d4d4" };

/**
 * Calendar Redesign (Task 2B) — the single source of truth for how a calendar
 * chip is painted. Every view (day/week/month/doctors/rooms + the shared
 * EventChip) routes its inline background/border/accent decisions through
 * {@link getEventStyle} so the "event-type" visuals live in exactly one place.
 *
 * Taxonomy split (locked): `type` = INTENT (clinic_visit / lead_call /
 * internal), `category` = BRAND (klea / other / …). Intent drives the chip's
 * FILL treatment (solid vs outlined) + a leading icon; brand drives a small
 * colored dot, but ONLY on multi-brand clinics — single-brand clinics keep the
 * whole-chip brand coloring they've always had.
 *
 * The whole surface is gated by `typed_events_enabled`. When the flag
 * is OFF, {@link getEventStyle} returns exactly today's behavior — the backend
 * `color` as a solid background, no icons, no accents — so the calendar is
 * pixel-identical to the pre-redesign build.
 */

/** A `@tabler/icons-react` component (same shape convention as appointment-form). */
type TablerIcon = typeof IconDental;

/**
 * Everything a chip needs to render itself, resolved centrally. Consumers merge
 * `style` onto their positioned inline style, append `className` + apply
 * `textClassName` via `cn()` (tailwind-merge lets these override the base
 * chip's `text-white`), render `Icon` as a leading glyph, draw `brandDotColor`
 * as a small dot, and append a check glyph when `showCompletedCheck`.
 */
export interface CalendarEventStyle {
  style: CSSProperties;
  /** Extra utility classes (opacity / line-through / dashed border helpers). */
  className: string;
  /** Text color override — '' keeps the base chip's own color (text-white). */
  textClassName: string;
  /** Leading intent/travel glyph, or null (flag-off / internal). */
  Icon: TablerIcon | null;
  /** Brand dot color — only set on multi-brand clinics; null otherwise. */
  brandDotColor: string | null;
  /** Trailing "completed" check glyph. */
  showCompletedCheck: boolean;
}

export interface GetEventStyleOptions {
  /** `feature_flags.typed_events_enabled` — gates ALL new visuals. */
  typesEnabled: boolean;
  /** Whether the visible event set spans more than one brand (see deriveMultiBrand). */
  multiBrand: boolean;
  /**
   * `appointment_presets.calendar.doctor_colors` — lets the treating doctor
   * drive the chip fill. Independent of `typesEnabled`: it applies on BOTH
   * styling paths, so a clinic can take doctor colours without taking the
   * event-types redesign. The backend already nulls `user.color` when the flag
   * is off; this is the second lock, so a cached payload can't leak colours in.
   */
  doctorColorsEnabled?: boolean;
}

/**
 * The doctor colour to paint this chip with, or null to leave the fill alone.
 *
 * Null covers three distinct cases that all mean the same thing to a chip:
 * the clinic is not on the feature, the appointment is booked against someone
 * who is not an active doctor, or this is not an appointment at all.
 */
function doctorColorFor(
  event: CalendarEvent,
  doctorColorsEnabled: boolean
): string | null {
  if (!doctorColorsEnabled) return null;
  if (event.eventType === "arrival" || event.eventType === "departure") return null;

  return event.user?.color ?? null;
}

/**
 * Category → dot hex. Covers every key in the fleet vocabulary — the same six
 * that `KNOWN_CATEGORY_KEYS` (appointment-form.tsx) labels and gives icons to,
 * and that backend/config/appointment_categories.php validates against.
 *
 * This map used to hold only klea + other, the pair that existed when the
 * calendar redesign shipped. The other four keys arrived later with the
 * checkup/hygiene/treatment/surgery list that seven clinics now run, and were
 * never added here — so four of five categories on those clinics all collapsed
 * onto the grey fallback and became indistinguishable. That is the bug this
 * fixes; the two original entries keep their exact hexes so nothing changes for
 * the clinics that were already correct.
 *
 * These are mid-tone 500 shades, NOT the darker 700s the doctor palette uses.
 * A brand dot carries no text and has to stay visible against both a dark chip
 * fill and a white popover/legend background, so mid-tone is right here for the
 * same reason dark is right there.
 *
 * Hues that already mean something else on this calendar are avoided: amber is
 * the pending fill, green and orange are the travel fills, red is the no-show
 * border. (klea's pink and surgery's rose are neighbours, which is tolerable
 * because they never co-occur — klea is YKM-only and surgery belongs to the
 * five-category list.)
 */
const BRAND_COLORS: Record<string, string> = {
  klea: "#ec4899", // pink — unchanged (YKM's legacy pair)
  other: "#3b82f6", // blue — unchanged (shared by every clinic)
  checkup: "#06b6d4", // cyan
  hygiene: "#10b981", // emerald
  treatment: "#8b5cf6", // violet
  surgery: "#f43f5e", // rose
};

/**
 * Ring for clinic-custom categories outside the fleet vocabulary. `getCategoryLabel`
 * already anticipates those (it falls back to showing the raw key), so they need a
 * colour too — grey is not one, since an unrecognised category is still a distinct
 * brand and painting it the same neutral as every other unrecognised one is exactly
 * the failure being fixed.
 */
const BRAND_CUSTOM_RING = [
  "#a855f7", // purple
  "#0ea5e9", // sky
  "#d946ef", // fuchsia
  "#14b8a6", // teal
  "#6366f1", // indigo
  "#84cc16", // lime
];

/** Reserved for "no category at all", which is not a brand and never gets a dot. */
const BRAND_FALLBACK = "#6b7280"; // gray

// Status + neutral accent colors.
const NEUTRAL_FILL = "#475569"; // slate-600 — the intent fill on multi-brand clinics
const NO_SHOW_RED = STATUS_HEX.no_show;

/**
 * Resolves a category to its dot hex. Known fleet keys get their pinned colour;
 * a clinic-custom key gets a stable colour off the custom ring; only a missing
 * category — a transfer or a blockout, which have no brand — falls through to
 * grey, and those never render a dot in the first place.
 */
export function brandColorFor(category?: string | null): string {
  if (!category) return BRAND_FALLBACK;
  if (BRAND_COLORS[category]) return BRAND_COLORS[category];

  // Stable per key so a custom category keeps its colour across renders and
  // sessions. A plain sum is enough: the ring only has to separate the handful
  // of custom keys one clinic might define, not hash a large space.
  let sum = 0;
  for (let i = 0; i < category.length; i++) sum += category.charCodeAt(i);

  return BRAND_CUSTOM_RING[sum % BRAND_CUSTOM_RING.length];
}

/** Appends a 2-digit hex alpha byte to a 6-digit hex color (#rrggbb → #rrggbbaa). */
function withAlpha(hex: string, alphaByte: string): string {
  return `${hex}${alphaByte}`;
}

/**
 * True when the visible appointments span more than one brand (`category`).
 * Travel markers carry no brand and are ignored. Drives the "brand dot instead
 * of whole-chip color" behavior — a single-brand clinic gets no dots.
 */
export function deriveMultiBrand(events: CalendarEvent[]): boolean {
  const brands = new Set<string>();
  for (const event of events) {
    if (event.eventType === "arrival" || event.eventType === "departure") continue;
    // An event with no category has no brand. This used to read `?? "klea"`,
    // which invented a YKM brand for every transfer and blockout on the board
    // — enough on its own to tip a genuinely single-brand clinic into the
    // multi-brand treatment and paint its chips neutral slate.
    if (!event.category) continue;
    brands.add(event.category);
    if (brands.size > 1) return true;
  }
  return false;
}

/**
 * The distinct brands present in the visible set, each with its dot color —
 * for the legend's brand row. Order-stable (insertion order of first sight).
 */
export function presentBrands(
  events: CalendarEvent[]
): { category: string; color: string }[] {
  const seen = new Map<string, string>();
  for (const event of events) {
    if (event.eventType === "arrival" || event.eventType === "departure") continue;
    // Same reason as deriveMultiBrand: a categoryless event is not a brand, and
    // defaulting it to "klea" listed a brand in the legend and the Show filter
    // that the clinic does not even have configured.
    const category = event.category;
    if (!category) continue;
    if (!seen.has(category)) seen.set(category, brandColorFor(category));
  }
  return [...seen.entries()].map(([category, color]) => ({ category, color }));
}

/**
 * The doctors present in the visible set, each with the colour their chips are
 * painted — for the legend's doctor row. Order-stable (insertion order of first
 * sight), deduped by user id, and limited to assignees the feed actually gave a
 * colour: an appointment booked against a coordinator has no doctor colour and
 * so has nothing to explain in a legend.
 */
export function presentDoctors(
  events: CalendarEvent[]
): { id: number; name: string; color: string }[] {
  const seen = new Map<number, { id: number; name: string; color: string }>();

  for (const event of events) {
    if (event.eventType === "arrival" || event.eventType === "departure") continue;

    const user = event.user;
    if (!user?.color || seen.has(user.id)) continue;

    seen.set(user.id, { id: user.id, name: user.full_name, color: user.color });
  }

  return [...seen.values()];
}

const FLAG_OFF_ICON: null = null;

/**
 * The centralized chip-styling decision. See the module doc for the model.
 *
 * Precedence (locked):
 *   1. flag off        → today's behavior (backend `color` fill), no extras —
 *                        except a doctor colour, which applies on this path too
 *                        (the two flags are independent).
 *   2. travel markers  → keep green/orange fill, add plane-arrival/-departure.
 *   3. doctor colour   → the treating doctor's colour becomes the fill, when the
 *                        clinic is on appointment_presets.calendar.doctor_colors and the
 *                        assignee is an active doctor.
 *   4. intent fill     → clinic_visit/null = solid; lead_call = outlined/lighter.
 *   5. status accents  → cancelled (50% + line-through), no_show (red dashed),
 *                        completed (trailing check) layered on top.
 *   6. brand dot       → only when multiBrand (else whole-chip brand color).
 */
export function getEventStyle(
  event: CalendarEvent,
  { typesEnabled, multiBrand, doctorColorsEnabled = false }: GetEventStyleOptions
): CalendarEventStyle {
  const doctorColor = doctorColorFor(event, doctorColorsEnabled);

  // (1) Flag off — reproduce the pre-redesign chip exactly, except that the
  // doctor colour still applies. `event.color` on this path already encodes
  // travel green/orange, which outranks the doctor, so the
  // override is limited to the brand/grey fill it would otherwise land on.
  if (!typesEnabled) {
    const keepLegacyFill =
      event.eventType === "arrival" ||
      event.eventType === "departure";

    // 87: status accents are STATUS, not "event-type visuals" — they apply on
    // both paths so the legend tells the truth on every clinic.
    const white = whiteTravelChip(event);
    return withStatusAccents(event, {
      style: {
        backgroundColor: !keepLegacyFill && doctorColor ? doctorColor : event.color,
        ...(white ? WHITE_CHIP_EDGE : {}),
      },
      className: "",
      textClassName: white ? WHITE_CHIP_TEXT : "",
      Icon: FLAG_OFF_ICON,
      brandDotColor: null,
      showCompletedCheck: false,
    });
  }

  // (2) Travel markers — unchanged fill, plane icons only.
  if (event.eventType === "arrival" || event.eventType === "departure") {
    const white = whiteTravelChip(event);
    return {
      style: { backgroundColor: event.color, ...(white ? WHITE_CHIP_EDGE : {}) },
      className: "",
      textClassName: white ? WHITE_CHIP_TEXT : "",
      Icon: event.eventType === "arrival" ? IconPlaneArrival : IconPlaneDeparture,
      brandDotColor: null,
      showCompletedCheck: false,
    };
  }

  const intent = event.type ?? "clinic_visit"; // null/legacy rows → clinic_visit
  const brand = brandColorFor(event.category);

  // The intent fill color: the treating
  // doctor, when the clinic is on doctor colours; otherwise a neutral slate on
  // multi-brand clinics (brand → dot) or the whole-chip brand color on
  // single-brand clinics (unchanged from today).
  //
  // The doctor slots in ABOVE the brand/neutral fill on purpose. Neutral slate is precisely the uniform block this feature exists
  // to break up — a multi-brand clinic painted every chip the same colour and
  // demoted brand to a dot, which is what the desk complained about. Brand is
  // not lost by the override: on a multi-brand clinic it is already carried by
  // the dot below, and on a single-brand clinic there is nothing to carry.
  const fill = doctorColor ?? (multiBrand ? NEUTRAL_FILL : brand);

  const style: CSSProperties = {};
  let textClassName = "";
  let Icon: TablerIcon | null = null;

  // (4) Intent fill treatment.
  if (intent === "lead_call") {
    // Outlined / lighter: subtle tinted background + a visible colored border,
    // dark (foreground) text + a phone glyph.
    style.backgroundColor = withAlpha(fill, "24"); // ~14% alpha
    style.border = `1px solid ${fill}`;
    textClassName = "text-foreground";
    Icon = IconPhone;
  } else {
    // clinic_visit: solid, white text, tooth glyph.
    style.backgroundColor = fill;
    Icon = IconDental;
  }

  // (6) Brand dot — multi-brand clinics only.
  const brandDotColor = multiBrand ? brand : null;

  // (5) Status accents layered on top.
  return withStatusAccents(event, {
    style,
    className: "",
    textClassName,
    Icon,
    brandDotColor,
    showCompletedCheck: false,
  });
}

/**
 * The status layer, shared by both styling paths: cancelled fades + strikes,
 * no-show gets the red dashed border (over any intent border), completed
 * gets the trailing check. Travel markers carry no status and pass through.
 */
function withStatusAccents(event: CalendarEvent, base: CalendarEventStyle): CalendarEventStyle {
  if (event.eventType === "arrival" || event.eventType === "departure") return base;
  const style: CSSProperties = { ...base.style };
  const className = event.status === "cancelled" ? `${base.className} line-through` : base.className;
  if (event.status === "cancelled") style.opacity = 0.5;
  if (event.status === "no_show") {
    style.border = `2px dashed ${NO_SHOW_RED}`;
  }
  return {
    ...base,
    style,
    className: className.trim(),
    showCompletedCheck: event.status === "completed",
  };
}

/**
 * 87: the week chip's category band — a 3px inset on the left edge in the
 * category colour. Returned as a boxShadow so it never fights the no-show
 * border. Null when the row has no category (a transfer, a blockout, a
 * categoryless legacy row) — nothing to signal.
 */
export function categoryBand(event: CalendarEvent): CSSProperties | null {
  if (event.eventType === "arrival" || event.eventType === "departure") return null;
  if (!event.category) return null;
  return { boxShadow: `inset 3px 0 0 0 ${brandColorFor(event.category)}` };
}

/** The medical-alert marker: one red dot with a white ring, readable on any fill. */
export const MEDICAL_DOT = "bg-red-500 ring-1 ring-white";

/**
 * Day-of check-in dots (85-02 stamps): amber = arrived, waiting; green = in
 * the chair. Dismissed rows are `completed` and carry the check instead.
 */
export const CHECKIN_DOT = {
  arrived: "bg-amber-400 ring-1 ring-white",
  seated: "bg-emerald-400 ring-1 ring-white",
} as const;

/** Which check-in dot a chip shows, if any. Only a live (scheduled) row has one. */
export function checkinDot(event: CalendarEvent): keyof typeof CHECKIN_DOT | null {
  if (event.status !== "scheduled") return null;
  if (event.time_seated) return "seated";
  if (event.time_arrived) return "arrived";
  return null;
}
